import {
  ref, computed, watch, onUnmounted,
} from 'vue';

import { kycStatus } from '@/api/kyc';
import { getMainInfo } from '@/api/main';
import { IFRAME_KYC_URL_LOCALSTORAGE_KEY, isAuthorized, userInfo } from '@/store/authorization';
import { isAccessAllowed } from '@/store/featureFlags';
import { ModalsEnum, openModal } from '@/store/modals';
import { pick } from '@/utils/helpers';

const transformProfit = (profit) => pick(profit, ['deposit', 'total', 'withdrawal']);

const transformInfo = ({
  agent: {
    id: userId,
    name: userName,
  },
  remain_limit: remainLimit,
  ...info
}) => ({
  userId,
  userName,
  remainLimit,
  profit: transformProfit(info.profit ?? {}),
  ...pick(info, ['balance', 'currency', 'limit', 'isConfirmAgentAgreement', 'paymentLimits', 'haveSubagents', 'blockDeposit', 'minLimit', 'bk_balance', 'miniCashbox', 'statusVerification', 'reasonsErrors']),
});

export const info = ref(null);

export const isInfoLoaded = computed(() => info.value !== null);

const NEGATIVE_VERIFICATION_STATUSES = [1, 2];
const POSITIVE_VERIFICATION_STATUSES = [3, 4];

export const isKYCCompleted = computed(
  () => POSITIVE_VERIFICATION_STATUSES.includes(info?.value?.statusVerification),
);
const KYC_STATUS_TIMEOUT = 300000;

export const getInfo = async () => {
  let kycStatusInterval;

  const response = await getMainInfo();

  info.value = transformInfo(response.data);
  isAccessAllowed.value = !response?.data?.miniCashbox;

  userInfo.id = info.value.userId;
  userInfo.statusVerification = info.value.statusVerification;
  userInfo.isConfirmAgentAgreement = info.value.isConfirmAgentAgreement;
  userInfo.reasonsErrors = info.value.reasonsErrors;

  if (Object.keys(info.value?.reasonsErrors ?? {}).length > 0
    && info.value.statusVerification === 1) {
    window.localStorage.removeItem(IFRAME_KYC_URL_LOCALSTORAGE_KEY);
  }

  if (NEGATIVE_VERIFICATION_STATUSES.includes(info.value.statusVerification)) {
    kycStatusInterval = setInterval(async () => {
      try {
        await kycStatus();
      } catch (e) {
        console.error(e);
      }
    }, KYC_STATUS_TIMEOUT);
  }

  if (POSITIVE_VERIFICATION_STATUSES.includes(userInfo?.statusVerification)) {
    clearInterval(kycStatusInterval);

    if (!info.value.isConfirmAgentAgreement) {
      openModal(ModalsEnum.RULES, { closable: false });
    }
  }

  onUnmounted(() => {
    clearInterval(kycStatusInterval);
  });
};

watch(isAuthorized, (value) => {
  if (value) {
    getInfo();
    return;
  }

  info.value = null;
});
